import type { ReactNode } from "react";
import { useAuth } from "../providers/AuthProvider";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Component to render while auth state is loading. Defaults to a simple loading message. */
  loadingComponent?: ReactNode;
}

/**
 * Route guard that redirects unauthenticated users to the IDP login via OIDC.
 *
 * Uses the OIDC context from AuthProvider. If the user is not authenticated,
 * initiates the OIDC login flow which redirects to the IDP.
 */
export function ProtectedRoute({ children, loadingComponent }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      loadingComponent ?? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-fg">Loading…</p>
        </div>
      )
    );
  }

  if (!isAuthenticated) {
    // Initiate OIDC login redirect
    // The current URL will be captured and returned to after login
    void login(window.location.pathname + window.location.search);
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-fg">Redirecting to login…</p>
      </div>
    );
  }

  return <>{children}</>;
}
